import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import CustomButton from "../../../../../../components/customButton/CustomButton";
import DiscountChip from "../../../../../../components/discountChip/DiscountChip";
import { useLanguage } from "../../../../../../hooks/languageContextHooks";
import { useAppSelector } from "../../../../../../hooks/reduxHooks";
import usePriceFormatter from "../../../../../../hooks/usePriceFormatter";
import {
  selectAdults,
  selectChildrensAgesList,
  selectCurrency,
  selectEnvironmentId,
} from "../../../../../../redux/slices/enviromentSlice/EnviromentSlice.selectors";
import { HotelDataListI } from "../../../../../../redux/slices/enviromentSlice/EnviromentSlice.type";

import { getMinPrice } from "../../../../../../utils/getPriceUtils";
import {
  FilterSortType,
  filterStateI,
} from "../../../../contexts/filterContext";
import styles from "./BookingPrice.module.scss";

export enum couponCodeDiscountTypesE {
  Percent = "Percent",
  Discount = "Discount",
}

const BookingPrice: React.FC<{
  allowAllGuestPrice: boolean;
  isOffer: boolean | "" | undefined;
  filterState: filterStateI;
  data: HotelDataListI;
  iniDate: string;
  endDate: string;
  isColumn?: boolean;
  onCheckAvailability: () => Promise<void>;
  onOpenAvailabilityPicker: React.Dispatch<React.SetStateAction<boolean>>;
  showHotelAvailabilityPicker: boolean;
  nights?: string;
  linkToProperty: string;
}> = ({
  allowAllGuestPrice,
  isOffer,
  filterState,
  data,
  iniDate,
  endDate,
  isColumn,
  onCheckAvailability,
  onOpenAvailabilityPicker,
  showHotelAvailabilityPicker,
  linkToProperty,
}) => {
  const currency = useAppSelector(selectCurrency);
  const environmentId = useAppSelector(selectEnvironmentId);
  const childrenAges = useAppSelector(selectChildrensAgesList).join(",");
  const adults = useAppSelector(selectAdults);
  const { language } = useLanguage();
  const { format } = usePriceFormatter();

  const { t } = useTranslation();

  const discountPrice =
    filterState.isFreeCancelable === FilterSortType.UNSET
      ? getMinPrice([
          data.priceNoOfferRefundable,
          data.priceNoOfferNotRefundable,
        ])
      : filterState.isFreeCancelable
      ? data.priceNoOfferRefundable
      : data.priceNoOfferNotRefundable;

  const price =
    filterState.isFreeCancelable === FilterSortType.UNSET
      ? getMinPrice([data.priceRefundable, data.priceNotRefundable])
      : filterState.isFreeCancelable
      ? data.priceRefundable
      : data.priceNotRefundable;

  const childrenAgesArray = childrenAges
    .split(",")
    .map((child) => (Number(child) === 0 ? null : Number(child)))
    .filter((child) => Boolean(child));

  const showDiscount =
    data.couponPercentDiscount || data.couponQuantityDiscount || undefined;

  if (allowAllGuestPrice) {
    return (
      <div className={styles.mainContainer}>
        <div className={`${styles.bookingPrice} ${styles.notAvailable}`}>
          <div className={styles.priceContainer}>
            <p>
              {t("labels.bookHereX")}{" "}
              {adults ? adults + " " + t("labels.adults") : ""}
              {childrenAgesArray.length > 0 && Boolean(adults) ? " y " : ""}
              {childrenAgesArray.length > 0
                ? childrenAgesArray.length === 1
                  ? childrenAgesArray.length + " " + t("labels.child")
                  : childrenAgesArray.length + " " + t("labels.children")
                : ""}
            </p>
            {price && (
              <>
                <p className={`price ${styles.price} ${styles.priceSpacing}`}>
                  {t("labels.from")} {currency}
                  {format(price)}
                </p>
                <p>{t("labels.nightsXGuest")}</p>
              </>
            )}
            <Link
              to={`${document.location.pathname}?${
                environmentId ? `environment=${environmentId}` : ""
              }&iniDate=${iniDate}&endDate=${endDate}&approute=property/${
                data.id
              }${childrenAges ? `&childrenAges=${childrenAges}` : ""}${
                adults ? `&adults=${2}` : ""
              }${language ? `&language=${language}` : ""}`}
              className={styles.bookNowBtn}
            >
              <CustomButton
                isLeft
                iconUrl="https://booking.availroom.com/v2/assets/icons/rightArrow.svg"
                className={`${styles.allowGuessButton} bookNowBtn`}
              >
                {t("property.bookNow")}
              </CustomButton>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (data.available) {
    return (
      <div className={styles.mainContainer}>
        <div className={`${styles.bookingPrice} ${isColumn && styles.mapview}`}>
          {!!showDiscount ? (
            <DiscountChip
              currency={currency}
              couponPercentDiscount={data.couponPercentDiscount}
              couponQuantityDiscount={data.couponQuantityDiscount}
            />
          ) : null}
          <div className={styles.priceContainer}>
            {isOffer && (
              <div className={styles.priceDiscount}>
                {currency}
                {format(discountPrice)}
              </div>
            )}
            <div className={`${styles.price} price`}>
              {currency}
              {format(price)}
            </div>
            <span className={styles.totalPrice}>
              {!isColumn && t("property.totalPrice")} ({data.nights}{" "}
              {t("property.nights")})
            </span>
          </div>
          <div className={styles.bookNowContainer}>
            <Link to={linkToProperty} className={styles.bookNowBtn}>
              <div className={styles.bookingButton}>
                <CustomButton
                  isLeft
                  iconUrl="https://booking.availroom.com/v2/assets/icons/rightArrow.svg"
                  className="bookNowBtn"
                >
                  {t("property.bookNow")}
                </CustomButton>
              </div>
            </Link>
            {filterState.isFreeCancelable &&
              (data.priceRefundable || data.priceNoOfferRefundable) && (
                <div className={styles.bookingCancelationFlag}>
                  <img
                    src="https://booking.availroom.com/v2/assets/icons/tick.svg"
                    alt="tick"
                  />
                  <span>{t("property.freeCancelation")}</span>
                </div>
              )}
          </div>
          <div className={styles.bookingMinimumPriceFlag}>
            <img
              src="https://booking.availroom.com/v2/assets/icons/shield.svg"
              alt="shield"
            />
            <span>{t("property.minimumPrice")}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.mainContainer}>
      <div className={`${styles.notAvailable} ${isColumn && styles.mapview}`}>
        <div className={styles.title}>
          {isColumn ? t("property.shortSoldOut") : t("property.soldOut")}
        </div>
        <div>
          <CustomButton
            onClick={() => {
              onCheckAvailability();
              onOpenAvailabilityPicker(!showHotelAvailabilityPicker);
            }}
            iconUrl="https://booking.availroom.com/v2/assets/icons/screen.svg"
            backgroundColor="#fece32"
            className={styles.checkAvailabilityBtn}
          >
            {t("property.checkAvailability")}
          </CustomButton>
        </div>
        <div className={styles.notAvailableTag}>
          <img
            src="https://booking.availroom.com/v2/assets/icons/shield.svg"
            alt="shield icon"
          />
          <span>{t("property.flexibleTravel")}</span>
        </div>
      </div>
    </div>
  );
};

export default BookingPrice;
